import { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d68bt65bd17lru29kpng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-playfair font-bold text-gray-900 mb-4">
            Kontakt & Öffnungszeiten
          </h2>
          <div className="w-24 h-1 bg-rose-300 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Kontaktinformationen */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-playfair font-semibold text-gray-900 mb-6">
                Kontaktinformationen
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-rose-400"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Adresse</h4>
                    <p className="text-gray-600 text-sm">
                      LunaCenter<br />
                      Wilhelm-Strauß-Weg 2B<br />
                      21109 Hamburg
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-phone-line text-2xl text-rose-400"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Telefon</h4>
                    <p className="text-gray-600 text-sm">
                      <a href="tel:04052479472" className="hover:text-rose-400 transition-colors whitespace-nowrap">
                        (040) 524 794 72
                      </a>
                      <br />
                      <a href="tel:+491625676111" className="hover:text-rose-400 transition-colors whitespace-nowrap">
                        +49 (0)162 567 6111
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-mail-line text-2xl text-rose-400"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">E-Mail</h4>
                    <a 
                      href="mailto:mail@loveyourselfbeauty.de" 
                      className="text-gray-600 hover:text-rose-400 transition-colors text-sm"
                    >
                      mail@loveyourselfbeauty.de
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-rose-400"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-base font-semibold text-gray-900 mb-2">Öffnungszeiten</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Montag - Samstag:</span>
                        <span className="text-gray-900 font-medium ml-4">10:00 - 18:00 Uhr</span>
                      </div>
                      <div className="text-gray-500 text-xs italic mt-1">
                        (nach Terminvereinbarung)
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-gray-600">Sonntag:</span>
                        <span className="text-gray-900 font-medium ml-4">Geschlossen</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="rounded-lg overflow-hidden shadow-lg h-80">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2372.5!2d9.9937!3d53.5074!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTPCsDMwJzI2LjYiTiA5wrA1OSczNy4zIkU!5e0!3m2!1sde!2sde!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Love yourself beauty by Meltem Standort"
              ></iframe>
            </div>
          </div>

          {/* Kontakt Links */}
          <div>
            <h3 className="text-2xl font-playfair font-semibold text-gray-900 mb-6">
              Schreiben Sie uns
            </h3>
            
            <p className="text-gray-600 mb-8 text-sm leading-relaxed">
              Kontaktieren Sie uns gerne über Instagram oder buchen Sie direkt Ihren Termin über Treatwell.
            </p>

            <div className="space-y-4">
              <a
                href="https://www.instagram.com/love_yourself_beauty_by_meltem/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 text-white px-8 py-5 rounded-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 font-medium text-base cursor-pointer whitespace-nowrap"
              >
                <i className="ri-instagram-line text-2xl mr-3"></i>
                Auf Instagram schreiben
              </a>

              <a
                href="https://buchung.treatwell.de/ort/445747/menue/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full bg-rose-400 text-white px-8 py-5 rounded-lg hover:bg-rose-500 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 font-medium text-base cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-2xl mr-3"></i>
                Termin auf Treatwell buchen
              </a>
            </div>

            <div className="mt-8 p-6 bg-rose-50 rounded-lg">
              <h4 className="text-base font-semibold text-gray-900 mb-3">
                <i className="ri-information-line text-rose-400 mr-2"></i>
                Schnelle Antwort garantiert
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Wir antworten in der Regel innerhalb von 24 Stunden auf Ihre Instagram-Nachrichten. Für eine sofortige Terminbuchung nutzen Sie bitte Treatwell.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
